import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

// SEO-optimised FAQ — each question targets a high-volume informational or
// commercial-intent query in the food & beverage / hospitality consulting space.
// Questions are also exposed to Google via FAQPage JSON-LD in /public/index.html.
const faqs = [
    {
        q: "How much does a food and beverage consultant cost?",
        a: "Eat + Drink Consulting works on transparent flat-rate packages instead of vague retainers. Our signature Event/Offer Monthly Strategy is $400 per month, our one-time Loyalty Program Setup and Monthly Subscription Box Setup are $500 each, and additional hourly consulting is $45 per hour. There are no long-term contracts and no surprise invoices — you only pay for the work you need.",
    },
    {
        q: "What does a restaurant consultant actually do?",
        a: "A great restaurant consultant does three things: finds where you're leaving money on the table, builds the programs and systems to capture it, and trains your team to run those programs without you. Day-to-day that looks like menu engineering, pricing strategy, event and promotion design, loyalty and subscription program builds, marketing campaign execution, SOP creation, POS configuration, and hands-on operator coaching. The goal is always the same: more revenue, more repeat guests, and more time back for the owner.",
    },
    {
        q: "How is monthly consulting different from hiring a marketing agency?",
        a: "A traditional marketing agency runs ads and posts to social media. We build the actual revenue program underneath — the event concept, the promotional offer, the loyalty mechanic, the upsell scripts, the staff training, and the financial targets — and only then activate it across email, SMS, and social. Restaurants don't usually need more ads; they need a better thing to advertise. That's what we deliver.",
    },
    {
        q: "How quickly will I see ROI from a consulting engagement?",
        a: "Most clients on the Event/Offer Monthly Strategy see incremental revenue within the first 30 days of their first launched event or offer. Loyalty programs and subscription boxes typically pay back the $500 setup within 60 to 90 days of launch through repeat visits and recurring membership revenue. We set explicit financial goals at the start of every engagement so ROI isn't a guess — it's a tracked number.",
    },
    {
        q: "Do you work with single-location restaurants or only restaurant groups?",
        a: "Both. The Event/Offer Monthly Strategy and à-la-carte hourly consulting are intentionally priced for independent operators — single restaurants, bars, cafés, and bottle shops. Multi-location groups, hotels, and hospitality brands typically combine the monthly strategy with hourly strategic work for menu engineering, brand refreshes, or systems overhauls across the portfolio.",
    },
    {
        q: "What is a restaurant loyalty program and is it worth setting one up?",
        a: "A loyalty program is a structured rewards mechanic — points, tiers, perks, or visit-based bonuses — designed to turn occasional customers into regulars. For most restaurants and bars it's the single highest-leverage revenue program available because acquiring a new guest costs five to seven times more than retaining one. Our $500 Loyalty Program Setup covers concept, POS integration, customer-facing copy, staff training, launch campaign, and tracking. Most clients recoup it in under three months.",
    },
    {
        q: "Can you help us launch a wine club, bottle club, or subscription box?",
        a: "Yes — this is one of our most-requested builds. The Monthly Subscription Box Setup covers wine clubs, beer and bottle clubs, meal kits, tasting clubs, subscription boxes, and member-perks programs. We design the membership tiers, pricing, fulfillment workflow, POS and payment setup, sales copy, launch campaign, and retention strategy. Subscription revenue is the most predictable income a hospitality business can build.",
    },
    {
        q: "What's the best way to increase restaurant revenue without raising prices?",
        a: "Three high-leverage levers in order of speed: launch a recurring monthly event or promotional offer that fills slow days, build a loyalty program that increases visit frequency from existing guests, and add a subscription or membership program that creates recurring monthly income. Menu engineering and upsell training stack on top of all three. Raising prices is almost always the last lever — not the first.",
    },
    {
        q: "Do you work remotely or only with local clients?",
        a: "Eat + Drink Consulting is remote-first and works with restaurants, bars, and hospitality brands worldwide. Strategy calls, campaign builds, copywriting, systems setup, and team training are all delivered over video and shared docs. On-site visits and hands-on launches can be arranged for clients who want them — just ask in your intro call.",
    },
    {
        q: "Can you work with our existing POS, email, and marketing tools?",
        a: "Yes. We're system-agnostic and configure inside whatever you already use — Toast, Square, Lightspeed, Resy, OpenTable, Mailchimp, Klaviyo, Eventbrite, Shopify, Tock, and most modern POS, CRM, and reservation platforms. If a tool change would meaningfully increase your revenue we'll say so — but we'd rather get the program running than sell you new software.",
    },
];

export default function FAQ() {
    const [open, setOpen] = useState(0);

    return (
        <section
            id="faq"
            data-testid="faq-section"
            className="py-24 md:py-32 bg-white border-t border-brand-border-light"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-6 mb-16 md:mb-20">
                    <div className="col-span-12 md:col-span-4 flex items-center gap-4">
                        <span className="h-px w-10 bg-brand-orange" />
                        <span className="text-xs uppercase tracking-[0.3em] text-brand-orange font-medium">
                            Frequently Asked
                        </span>
                    </div>
                    <h2
                        data-testid="faq-headline"
                        className="col-span-12 md:col-span-8 font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-brand-dark"
                    >
                        Answers before
                        <br />
                        <span className="italic text-brand-muted">the discovery call.</span>
                    </h2>
                </div>

                <div className="grid grid-cols-12 gap-6 md:gap-10">
                    <div className="col-span-12 lg:col-span-10 lg:col-start-2">
                        <ul className="border-t border-brand-border-light">
                            {faqs.map((f, i) => {
                                const isOpen = open === i;
                                return (
                                    <li
                                        key={f.q}
                                        data-testid={`faq-item-${i}`}
                                        className="border-b border-brand-border-light"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setOpen(isOpen ? -1 : i)}
                                            data-testid={`faq-toggle-${i}`}
                                            aria-expanded={isOpen}
                                            className="w-full flex items-start justify-between gap-4 sm:gap-6 py-6 md:py-8 text-left group"
                                        >
                                            <span
                                                className={`flex-1 min-w-0 font-serif text-lg sm:text-xl md:text-2xl leading-snug break-words transition-colors ${
                                                    isOpen
                                                        ? "text-brand-orange"
                                                        : "text-brand-dark group-hover:text-brand-orange"
                                                }`}
                                            >
                                                {f.q}
                                            </span>
                                            <span
                                                className={`mt-1 shrink-0 w-9 h-9 border flex items-center justify-center transition-colors ${
                                                    isOpen
                                                        ? "border-brand-orange text-brand-orange"
                                                        : "border-brand-border-light text-brand-dark group-hover:border-brand-orange group-hover:text-brand-orange"
                                                }`}
                                            >
                                                {isOpen ? (
                                                    <Minus className="w-4 h-4" />
                                                ) : (
                                                    <Plus className="w-4 h-4" />
                                                )}
                                            </span>
                                        </button>
                                        <AnimatePresence initial={false}>
                                            {isOpen && (
                                                <motion.div
                                                    key="content"
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: "auto", opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    transition={{ duration: 0.35, ease: "easeOut" }}
                                                    className="overflow-hidden"
                                                >
                                                    <p
                                                        data-testid={`faq-answer-${i}`}
                                                        className="text-brand-muted leading-relaxed text-base md:text-lg pb-8 max-w-3xl"
                                                    >
                                                        {f.a}
                                                    </p>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </li>
                                );
                            })}
                        </ul>

                        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8">
                            <p className="text-brand-muted text-base">
                                Still have a question?
                            </p>
                            <a
                                href="#contact"
                                data-testid="faq-cta-contact"
                                className="inline-flex items-center gap-2 border border-brand-dark text-brand-dark px-6 py-3 text-xs uppercase tracking-[0.25em] hover:bg-brand-dark hover:text-brand-cream transition-colors"
                            >
                                Ask Amy directly
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
